import React, { useEffect } from 'react';
import { XIcon, LockIcon, ShieldCheckIcon, DownloadIcon, PrinterIcon } from 'lucide-react';
import { Document } from '../types/documents';
interface DocumentPreviewProps {
  document: Document;
  onClose: () => void;
}
export function DocumentPreview({
  document,
  onClose
}: DocumentPreviewProps) {
  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-[#181818]/70 backdrop-blur-sm transition-opacity" data-id="element-8">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" role="dialog" aria-modal="true" aria-labelledby="preview-title" data-id="element-9">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#DDDBDA] bg-white" data-id="element-10">
          <div className="flex items-center space-x-4" data-id="element-11">
            <div className="flex flex-col" data-id="element-12">
              <h2 id="preview-title" className="text-lg font-semibold text-[#181818]" data-id="element-13">
                {document.name}
              </h2>
              <div className="flex items-center text-sm text-[#706E6B] mt-0.5 space-x-2" data-id="element-14">
                <span data-id="element-15">{document.type}</span>
                <span data-id="element-16">&bull;</span>
                <span data-id="element-17">{document.date}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2" data-id="element-18">
            <button className="hidden md:flex items-center px-3 py-1.5 bg-white text-[#444444] border border-[#DDDBDA] rounded text-sm font-medium hover:bg-[#F3F3F3] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0176D3]" title="Print document" data-id="element-19">
              <PrinterIcon className="w-4 h-4 mr-1.5" data-id="element-20" />
              Print
            </button>
            <button className="hidden md:flex items-center px-3 py-1.5 bg-[#0176D3] text-white rounded text-sm font-medium hover:bg-[#015BA1] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0176D3] focus:ring-offset-1" title="Download document" data-id="element-21">
              <DownloadIcon className="w-4 h-4 mr-1.5" data-id="element-22" />
              Download
            </button>
            <div className="hidden md:block w-px h-6 bg-[#DDDBDA] mx-1" data-id="element-23"></div>
            <button onClick={onClose} className="p-2 text-[#706E6B] hover:text-[#181818] hover:bg-[#F3F3F3] rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0176D3]" aria-label="Close preview" data-id="element-24">
              <XIcon className="w-5 h-5" data-id="element-25" />
            </button>
          </div>
        </div>

        {/* Security Banner */}
        <div className="bg-[#E3F2FD] border-b border-[#0176D3]/20 px-6 py-2 flex items-center justify-center text-sm text-[#0176D3] font-medium" data-id="element-26">
          <LockIcon className="w-4 h-4 mr-2" data-id="element-27" />
          Secure Access — You have full document permissions including download
          and print.
        </div>

        {/* Mock Document Viewer Area */}
        <div className="flex-1 bg-[#ECEBEA] overflow-y-auto p-4 sm:p-8 flex justify-center custom-scrollbar" data-id="element-28">
          {/* Simulated PDF Page */}
          <div className="bg-white shadow-md w-full max-w-3xl min-h-[1056px] p-12 flex flex-col relative" data-id="element-29">
            {/* Watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] overflow-hidden" data-id="element-30">
              <div className="transform -rotate-45 text-8xl font-bold text-black whitespace-nowrap" data-id="element-31">
                MADISON MORTGAGE SECURE
              </div>
            </div>

            {/* Mock Content */}
            <div className="border-b-2 border-gray-800 pb-6 mb-8 flex justify-between items-end" data-id="element-32">
              <div data-id="element-33">
                <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-34">
                  {document.type}
                </h1>
                <p className="text-gray-600" data-id="element-35">Document ID: {document.id}</p>
              </div>
              <div className="text-right" data-id="element-36">
                <ShieldCheckIcon className="w-12 h-12 text-gray-300 inline-block mb-2" data-id="element-37" />
                <p className="text-sm text-gray-500 font-mono" data-id="element-38">VERIFIED COPY</p>
              </div>
            </div>

            <div className="space-y-6 text-gray-800" data-id="element-39">
              <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-40">
                <div data-id="element-41">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-42">
                    Applicant Name
                  </h3>
                  <p className="font-medium border-b border-gray-300 pb-1" data-id="element-43">
                    Johnson Family
                  </p>
                </div>
                <div data-id="element-44">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-45">
                    Date Submitted
                  </h3>
                  <p className="font-medium border-b border-gray-300 pb-1" data-id="element-46">
                    {document.date}
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-47">
                <div className="h-4 bg-gray-200 rounded w-full" data-id="element-48"></div>
                <div className="h-4 bg-gray-200 rounded w-full" data-id="element-49"></div>
                <div className="h-4 bg-gray-200 rounded w-11/12" data-id="element-50"></div>
                <div className="h-4 bg-gray-200 rounded w-full" data-id="element-51"></div>
                <div className="h-4 bg-gray-200 rounded w-4/5" data-id="element-52"></div>
              </div>

              <div className="py-8" data-id="element-53">
                <div className="h-32 bg-gray-100 border border-gray-200 rounded flex items-center justify-center text-gray-400" data-id="element-54">
                  [ Data Table Redacted for Security ]
                </div>
              </div>

              <div className="space-y-4" data-id="element-55">
                <div className="h-4 bg-gray-200 rounded w-full" data-id="element-56"></div>
                <div className="h-4 bg-gray-200 rounded w-10/12" data-id="element-57"></div>
                <div className="h-4 bg-gray-200 rounded w-full" data-id="element-58"></div>
                <div className="h-4 bg-gray-200 rounded w-9/12" data-id="element-59"></div>
              </div>
            </div>

            <div className="mt-auto pt-12 border-t border-gray-200 flex justify-between text-xs text-gray-400 font-mono" data-id="element-60">
              <span data-id="element-61">Page 1 of 1</span>
              <span data-id="element-62">Generated: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}